import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, PlusCircle, Phone, Mail, X, Save } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Customers() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {id?, name, phone, email, notes}

  async function refresh() {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers${q ? `?search=${encodeURIComponent(q)}` : ""}`);
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(refresh, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Todos los roles internos pueden CREAR un cliente.
  const canCreate = ["admin", "supervisor", "reception", "technician"].includes(user.role);
  // Editar/eliminar clientes existentes sigue restringido.
  const canManage = ["admin", "supervisor", "reception"].includes(user.role);

  async function save() {
    if (!modal.name?.trim() || !modal.phone?.trim()) {
      toast.error("Nombre y teléfono son obligatorios");
      return;
    }
    try {
      if (modal.id) {
        await api.patch(`/customers/${modal.id}`, {
          name: modal.name,
          phone: modal.phone,
          email: modal.email,
          notes: modal.notes,
        });
        toast.success("Cliente actualizado");
      } else {
        await api.post("/customers", modal);
        toast.success("Cliente creado");
      }
      setModal(null);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight">Clientes</h1>
          <p className="text-slate-500 text-sm mt-1">Ficha de clientes e historial.</p>
        </div>
        {canCreate && (
          <button onClick={() => setModal({ name: "", phone: "", email: "", notes: "" })} className="btn-primary" data-testid="customers-new-button">
            <PlusCircle size={18} /> Nuevo cliente
          </button>
        )}
      </div>

      <div className="card-base p-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, teléfono o email…"
            className="input-base !pl-11"
            data-testid="customers-search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-slate-500 font-mono">Cargando…</div>
      ) : list.length === 0 ? (
        <div className="card-base p-10 text-center text-slate-500">Sin clientes.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {list.map((c) => (
            <button
              key={c.id}
              onClick={() => canManage && setModal({ ...c })}
              className="card-interactive p-4 text-left"
              data-testid={`customer-card-${c.id}`}
            >
              <div className="font-heading font-bold text-lg truncate">{c.name}</div>
              <div className="flex items-center gap-1 text-sm text-slate-500 mt-1">
                <Phone size={12} /> <span className="font-mono">{c.phone}</span>
              </div>
              {c.email && (
                <div className="flex items-center gap-1 text-xs text-slate-500 mt-0.5 truncate">
                  <Mail size={12} /> {c.email}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <Modal onClose={() => setModal(null)} title={modal.id ? "Editar cliente" : "Nuevo cliente"}>
          <div className="space-y-3">
            <input value={modal.name} onChange={(e) => setModal({ ...modal, name: e.target.value })} placeholder="Nombre *" className="input-base" required data-testid="customer-modal-name" />
            <input value={modal.phone} onChange={(e) => setModal({ ...modal, phone: e.target.value })} placeholder="Teléfono * (obligatorio)" className="input-base" required data-testid="customer-modal-phone" />
            <input value={modal.email || ""} onChange={(e) => setModal({ ...modal, email: e.target.value })} placeholder="Email (opcional)" className="input-base" data-testid="customer-modal-email" />
            <textarea value={modal.notes || ""} onChange={(e) => setModal({ ...modal, notes: e.target.value })} placeholder="Notas" className="input-base min-h-[80px]" data-testid="customer-modal-notes" />
            <button onClick={save} className="btn-primary w-full" data-testid="customer-modal-save">
              <Save size={16} /> Guardar
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-base p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-xl">{title}</h3>
          <button onClick={onClose} className="btn-ghost" data-testid="modal-close"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
